/**
 * "Find creators" for one folder: the schema-driven search form, saved
 * searches, and the running search with its overlap and close-calls panels.
 *
 * The pieces live in src/discovery/search/**; this file only holds state and
 * wires them together. Exported name and props are unchanged so the folder
 * workspace can render it as before.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type JSX, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Search as SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FieldApiError } from '../api/client';
import type { NearMissOption, SavedSearch, SearchHistoryEntry, SearchParamValue } from '../api/types';
import { activeJobKey, useActiveJob, useSearchHistory } from '../hooks/scrape';
import { useCapabilities } from '../hooks/capabilities';
import { useCreateSchedule, useSchedules, useUpdateSchedule } from '../hooks/schedules';
import { SchedulePanel } from '../components/SchedulePanel';
import { TermSuggestions } from '../components/AudiencePanel';
import { toPlatform, type Platform } from '../lib/platform';
import { useFolderLeadSample, useSearchApi, useSearchJob, useSearchSchema, useStartSearch } from '../search/hooks';
import { applyNearMiss } from '../search/insights';
import { JobPanel } from '../search/JobPanel';
import { SoundList } from '../components/SoundLink';
import { parseSoundId, type SoundRef } from '../lib/sounds';
import { SearchForm } from '../search/SearchForm';
import {
  activeFilters,
  defaultParams,
  fromScrapeParams,
  groupsWithErrors,
  hasQuery,
  initialFormState,
  isKeywordLike,
  keywordLines,
  mapFieldErrors,
  querySummary,
  searchName,
  toScrapeParams,
  validateForm,
  withPlatform,
  type MappedFieldErrors,
  type SearchFormState,
} from '../search/params';

interface Props {
  folderPublicId: string;
  /** Drives the default open/closed state — an empty folder leads with search. */
  folderIsEmpty: boolean;
  /** Shown in "Include selected in …". Optional; falls back to "this folder". */
  folderName?: string | null;
}

const NO_SERVER_ERRORS: MappedFieldErrors = { filters: {}, query: null, other: [] };

export function SearchPanel({ folderPublicId, folderIsEmpty, folderName }: Props): JSX.Element {
  const qc = useQueryClient();
  const { schema, isFallback, isLoading: schemaLoading } = useSearchSchema();
  const capabilities = useCapabilities();
  const leadSample = useFolderLeadSample(folderPublicId);
  const historyQuery = useSearchHistory(folderPublicId);
  const activeJobQuery = useActiveJob(folderPublicId);
  const start = useStartSearch();
  const createSchedule = useCreateSchedule(folderPublicId);
  const updateSchedule = useUpdateSchedule(folderPublicId);
  const folderSchedules = useSchedules(folderPublicId);
  const searchApi = useSearchApi();

  const [form, setForm] = useState<SearchFormState | null>(null);
  const [open, setOpen] = useState(folderIsEmpty);
  const [repeat, setRepeat] = useState({ enabled: false, everyDays: '1' });
  const [presetId, setPresetId] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<MappedFieldErrors>(NO_SERVER_ERRORS);
  const [jobId, setJobId] = useState<string | null>(null);
  const [ranWith, setRanWith] = useState<Record<string, unknown> | null>(null);
  // Dismissing remembers WHICH job: useActiveJob keeps returning a finished
  // job for a while, so clearing jobId alone would bring the panel back.
  const [dismissedJobId, setDismissedJobId] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // The form starts once the schema has settled (server or fallback), so it
  // never re-initialises under someone who's typing.
  useEffect(() => {
    if (!form && !schemaLoading) setForm(initialFormState(schema));
  }, [form, schemaLoading, schema]);

  useEffect(() => {
    setJobId(null);
    setDismissedJobId(null);
    setOpen(folderIsEmpty);
  }, [folderPublicId, folderIsEmpty]);

  // A folder of Instagram creators should open an Instagram search. Applied
  // at most once per folder, only while the form is untouched.
  const [platformTouched, setPlatformTouched] = useState(false);
  const [defaultAppliedFor, setDefaultAppliedFor] = useState<string | null>(null);
  useEffect(() => setPlatformTouched(false), [folderPublicId]);
  useEffect(() => {
    if (!form || defaultAppliedFor === folderPublicId || platformTouched || !leadSample.data) return;
    setDefaultAppliedFor(folderPublicId);
    if (hasQuery(form) || leadSample.data.length === 0) return;
    const counts = new Map<Platform, number>();
    for (const l of leadSample.data) counts.set(toPlatform(l.platform), (counts.get(toPlatform(l.platform)) ?? 0) + 1);
    const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    if (top && schema.platforms.some((p) => p.id === top && p.available)) setForm(withPlatform(schema, form, top));
  }, [form, leadSample.data, platformTouched, folderPublicId, defaultAppliedFor, schema]);

  const activeJobId = activeJobQuery.data?.jobId ?? null;
  const effectiveJobId = jobId ?? (activeJobId && activeJobId !== dismissedJobId ? activeJobId : null);
  const job = useSearchJob(effectiveJobId, folderPublicId);
  const showJob = !!effectiveJobId && job.status !== 'idle';

  const clientErrors = useMemo(() => (form ? validateForm(schema, form) : {}), [schema, form]);
  const errors = useMemo(() => {
    const { query: _q, ...rest } = clientErrors;
    return { ...rest, ...serverErrors.filters };
  }, [clientErrors, serverErrors]);
  const queryError = serverErrors.query ?? (attempted ? clientErrors.query ?? null : null);
  const active = useMemo(() => (form ? activeFilters(schema, form) : []), [schema, form]);
  const forceOpen = useMemo(() => groupsWithErrors(schema, errors), [schema, errors]);

  const folderSeeds = useMemo(
    () => (form ? (leadSample.data ?? []).filter((l) => toPlatform(l.platform) === form.platform).map((l) => l.username) : []),
    [leadSample.data, form],
  );

  function dismissJob() {
    setJobId(null);
    setDismissedJobId(effectiveJobId);
  }

  function changeForm(next: SearchFormState) {
    setForm(next);
    if (serverErrors.query || serverErrors.other.length) setServerErrors((e) => ({ ...e, query: null, other: [] }));
  }

  function changeParam(key: string, v: SearchParamValue) {
    setForm((f) => (f ? { ...f, params: { ...f.params, [key]: v } } : f));
    if (serverErrors.filters[key]) {
      setServerErrors((e) => {
        const { [key]: _gone, ...filters } = e.filters;
        return { ...e, filters };
      });
    }
  }

  function loadParams(params: Record<string, unknown>, fromPreset: string | null) {
    setForm(fromScrapeParams(schema, params));
    setPresetId(fromPreset);
    setServerErrors(NO_SERVER_ERRORS);
    setAttempted(false);
    setPlatformTouched(true);
    setOpen(true);
  }

  // A schedule in this folder with the name this search would get: ticking
  // "Repeat this search" updates it instead of adding a duplicate.
  const existingSchedule = useMemo(() => {
    if (!form) return null;
    const name = searchName(form).trim().toLowerCase();
    const rows = Array.isArray(folderSchedules.data) ? folderSchedules.data : [];
    return rows.find((x) => x.name.trim().toLowerCase() === name) ?? null;
  }, [form, folderSchedules.data]);

  function submit(ev: FormEvent) {
    ev.preventDefault();
    if (!form) return;
    setAttempted(true);
    // §6: nothing is scheduled (or started) until the form validates.
    if (Object.keys(clientErrors).length > 0) return;
    const params = toScrapeParams(schema, form, folderPublicId, { presetId });
    const scheduleName = searchName(form);
    start.mutate(params, {
      onSuccess: ({ jobId: id }) => {
        setJobId(id);
        setRanWith(params as unknown as Record<string, unknown>);
        setDismissedJobId(null);
        setServerErrors(NO_SERVER_ERRORS);
        setAttempted(false);
        setOpen(false);
        void qc.invalidateQueries({ queryKey: ['search-history', folderPublicId] });
        void qc.invalidateQueries({ queryKey: activeJobKey(folderPublicId) });
        // …and the schedule only once the search itself has been accepted.
        if (repeat.enabled) {
          const everyDays = Math.min(Math.max(Number(repeat.everyDays) || 1, 1), 90);
          const every = `every ${repeat.everyDays} day${repeat.everyDays === '1' ? '' : 's'}`;
          const notSaved = (err: unknown) =>
            toast.error(`The search started, but the schedule wasn't saved: ${(err as Error).message}`);
          if (existingSchedule) {
            // Same search, same folder: change the schedule that's there rather
            // than leave two running side by side with different settings.
            searchApi.scheduleSettings
              .save(existingSchedule.publicId, params)
              .then(() =>
                updateSchedule.mutate(
                  { publicId: existingSchedule.publicId, patch: { everyDays } },
                  { onSuccess: () => toast.success(`Updated "${existingSchedule.name}" to these settings, repeating ${every}`), onError: notSaved },
                ),
              )
              .catch(notSaved);
          } else {
            createSchedule.mutate(
              { name: scheduleName, everyDays, params },
              { onSuccess: () => toast.success(`Scheduled to repeat ${every}`), onError: notSaved },
            );
          }
          setRepeat((r) => ({ ...r, enabled: false }));
        }
      },
      onError: (err) => {
        if (err instanceof FieldApiError && err.fieldErrors) {
          setServerErrors(mapFieldErrors(err.fieldErrors, schema));
          setOpen(true);
        }
      },
    });
  }

  function applyNearMissToForm(o: NearMissOption) {
    setForm((f) => (f ? { ...f, params: applyNearMiss(schema, f.params, o) } : f));
    setOpen(true);
    toast.message('Thresholds updated in the form. Press Search when ready.');
    requestAnimationFrame(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  const history: SearchHistoryEntry[] = Array.isArray(historyQuery.data) ? historyQuery.data : [];

  const addons = useMemo<Record<string, ReactNode>>(() => {
    if (!form) return {};
    return {
      audienceCountries: (
        <TermSuggestions
          platform={form.platform}
          topics={keywordLines(form.query)}
          countries={Array.isArray(form.params.audienceCountries) ? form.params.audienceCountries : []}
          keywordMode={isKeywordLike(form.mode) && form.mode !== 'lists'}
          onAddTerm={(term) =>
            setForm((f) => {
              if (!f) return f;
              const lines = keywordLines(f.query);
              if (lines.some((l) => l.toLowerCase() === term.toLowerCase())) return f;
              return { ...f, query: [...lines, term].join('\n') };
            })
          }
        />
      ),
    };
  }, [form]);

  // Fails safe: a capabilities outage greys the box out rather than offering
  // a filter the server would quietly skip.
  const disabled = useMemo(() => {
    const out: Record<string, string> = {};
    if (!capabilities.presenterCheck) out.presenterFilter = 'Not configured on this server';
    return out;
  }, [capabilities.presenterCheck]);

  const startError =
    start.error && !(start.error instanceof FieldApiError && start.error.fieldErrors) ? (start.error as Error).message : null;

  return (
    <section className="panel rounded-xl" aria-label="Find creators">
      {/* Always-visible header: what the search is set to, and one way in. */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-5">
        {/* The summary sits beside the toggle, not inside it: sound names in it are links. */}
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex shrink-0 items-center gap-2.5 text-left"
            aria-expanded={open}
          >
            <SearchIcon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            <h2 className="shrink-0 text-[15px] font-bold">Find creators</h2>
          </button>
          {!open && form && (
            <span className="min-w-0 cursor-pointer truncate text-sm text-muted-foreground" onClick={() => setOpen(true)}>
              <QuerySummaryLine schema={schema} form={form} />
              {active.length > 0 && ` · ${active.length} filter${active.length === 1 ? '' : 's'}`}
            </span>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="ml-auto shrink-0"
            aria-expanded={open}
            aria-label={open ? 'Collapse the search form' : 'Expand the search form'}
          >
            <ChevronDown
              className={cn('h-4 w-4 text-muted-foreground transition-transform duration-150', open && 'rotate-180')}
              strokeWidth={2}
            />
          </button>
        </div>
        {!open && (
          <Button size="sm" onClick={() => setOpen(true)}>
            New search
          </Button>
        )}
      </div>

      {showJob && (
        <div className="border-t border-border px-4 py-3 sm:px-5">
          <JobPanel
            state={job}
            jobId={effectiveJobId}
            folderPublicId={folderPublicId}
            folderName={folderName}
            schema={schema}
            ranWith={ranWith}
            onApplyNearMiss={applyNearMissToForm}
            onSearchAgain={() => {
              dismissJob();
              setOpen(true);
            }}
            onDone={() => {
              dismissJob();
              setOpen(false);
            }}
          />
        </div>
      )}

      {open && (
        <div ref={formRef}>
          {!form ? (
            <p className="border-t border-border px-5 py-6 text-sm text-muted-foreground">Loading the search form…</p>
          ) : (
            <SearchForm
              schema={schema}
              isFallback={isFallback}
              state={form}
              onStateChange={changeForm}
              onParamChange={changeParam}
              onPlatformChange={(p) => {
                setPlatformTouched(true);
                changeForm(withPlatform(schema, form, p));
              }}
              errors={errors}
              queryError={queryError}
              otherErrors={serverErrors.other}
              forceOpen={forceOpen}
              active={active}
              disabled={disabled}
              addons={addons}
              folderSeeds={folderSeeds}
              repeat={repeat}
              repeatUpdates={existingSchedule?.name ?? null}
              onRepeatChange={setRepeat}
              history={history}
              onLoadHistory={(e) => loadParams(e.params, null)}
              aside={<SchedulePanel folderPublicId={folderPublicId} />}
              onApplySaved={(s: SavedSearch) => loadParams(s.params, s.id)}
              savedParams={() => {
                const { folderId: _f, presetId: _p, ...rest } = toScrapeParams(schema, form, folderPublicId) as unknown as Record<string, unknown>;
                return rest;
              }}
              suggestedName={searchName(form)}
              canSave={hasQuery(form)}
              onReset={() => {
                setForm(initialFormState(schema, form.platform));
                setPresetId(null);
                setServerErrors(NO_SERVER_ERRORS);
                setAttempted(false);
              }}
              onClearFilters={() => setForm({ ...form, params: defaultParams(schema) })}
              onSubmit={submit}
              submitting={start.isPending}
              submitError={startError}
            />
          )}
        </div>
      )}
    </section>
  );
}

/** The collapsed header's "TikTok · …", with a sound search's sounds as links. */
function QuerySummaryLine({ schema, form }: { schema: Parameters<typeof querySummary>[0]; form: SearchFormState }): JSX.Element {
  if (form.mode !== 'sound') return <>{querySummary(schema, form)}</>;
  const platform = form.platform === 'instagram' ? 'instagram' : 'tiktok';
  const refs: SoundRef[] =
    platform === 'tiktok'
      ? form.sounds.map((s) => ({ platform, id: s.musicId }))
      : form.query
          .split('\n')
          .map((l) => parseSoundId(platform, l))
          .filter((id): id is string => !!id)
          .map((id) => ({ platform, id }));
  if (refs.length === 0) return <>{querySummary(schema, form)}</>;
  const label = schema.platforms.find((p) => p.id === form.platform)?.label ?? form.platform;
  return (
    <>
      {label} · <SoundList sounds={refs} max={3} />
    </>
  );
}
